/**
 * iTwin Scene Management Module
 *
 * This module contains all iTwin-related scene loading, model transformation,
 * UI controls (height/rotation sliders), and terrain utility functions.
 *
 * Exported functions used directly in Scenes.js:
 *   - validateITwinConfig
 *   - hideITwinHeightSlider
 *
 * Window globals registered here:
 *   - window.itwinTileset
 *   - window.loadITwinScene
 *   - window.relocateITwinModel
 *   - window.rotateITwinModel
 *   - window.getTerrainHeight
 *   - window.getHeightAboveTerrain
 */

import { getBaseUrl } from '../../envUtils';

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate iTwin configuration object
 * @param {object} itwinConfig - iTwin configuration from backend
 * @returns {object} - { valid: boolean, errors: string[] }
 */
export const validateITwinConfig = (itwinConfig) => {
    const errors = [];

    if (!itwinConfig) {
        return { valid: false, errors: ['iTwin configuration is missing'] };
    }

    const requiredFields = ['clientId', 'secret', 'iModelId', 'iTwinId', 'ionToken'];

    requiredFields.forEach(field => {
        if (!itwinConfig[field] || (typeof itwinConfig[field] === 'string' && itwinConfig[field].trim() === '')) {
            errors.push(`${field} is required`);
        }
    });

    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (itwinConfig.iModelId && !uuidRegex.test(itwinConfig.iModelId)) {
        errors.push('iModelId is not a valid UUID');
    }

    if (itwinConfig.iTwinId && !uuidRegex.test(itwinConfig.iTwinId)) {
        errors.push('iTwinId is not a valid UUID');
    }

    return {
        valid: errors.length === 0,
        errors
    };
};

// ============================================================================
// CESIUM LOADER
// ============================================================================

/**
 * Load iTwin-compatible Cesium library dynamically
 * @param {string} version - Cesium version to load
 * @returns {Promise<object>} - Resolves with Cesium object
 */
const loadCesiumForITwin = (version) => {
    return new Promise((resolve, reject) => {
        if (window.CesiumITwin) {
            resolve(window.CesiumITwin);
            return;
        }

        const baseUrl = `https://twinworld-component.s3.amazonaws.com/build/cesium_itwin/Cesium-${version}`;

        // Load CSS
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = `${baseUrl}/Build/Cesium/Widgets/widgets.css`;
        document.head.appendChild(css);

        // Load JS
        const script = document.createElement('script');
        script.src = `${baseUrl}/Build/Cesium/Cesium.js`;
        script.onload = () => {
            window.Cesium.buildModuleUrl.setBaseUrl(`${baseUrl}/Build/Cesium/`);
            window.CesiumITwin = window.Cesium;
            resolve(window.Cesium);
        };
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

// ============================================================================
// UI CONTROLS: HEIGHT & ROTATION SLIDERS
// ============================================================================

/**
 * Show iTwin model controls panel (height + rotation sliders + save)
 * @param {number} relative_height_start - Initial height offset in meters
 * @param {number} rotationX_start - Initial X rotation in degrees
 * @param {number} rotationY_start - Initial Y rotation in degrees
 * @param {number} rotationZ_start - Initial Z rotation in degrees
 */
function showITwinHeightSlider(relative_height_start, rotationX_start, rotationY_start, rotationZ_start) {
    const shadowRoot = document.querySelector('twinworld-app')?.shadowRoot;
    if (!shadowRoot) {
        console.warn('Cannot show sliders: twinworld-app shadowRoot not found');
        return;
    }

    // Remove existing container if any
    const existingContainer = shadowRoot.querySelector('#itwin-controls-container');
    if (existingContainer) {
        existingContainer.remove();
    }

    // Create main container for all controls
    const controlsContainer = document.createElement('div');
    controlsContainer.id = 'itwin-controls-container';
    controlsContainer.style.cssText = `
        position: fixed;
        top: 50px;
        right: 20px;
        background: rgba(0, 0, 0, 0.7);
        padding: 15px 20px;
        border-radius: 8px;
        color: white;
        font-family: 'Suisse Intl', sans-serif;
        z-index: 1000;
        min-width: 320px;
        max-height: 90vh;
        overflow-y: auto;
        transition: all 0.3s ease;
    `;

    // Create title row with hide button
    const titleRow = document.createElement('div');
    titleRow.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.3);
        padding-bottom: 10px;
    `;

    const title = document.createElement('div');
    title.style.cssText = `
        font-size: 16px;
        font-weight: 600;
    `;
    title.textContent = 'iTwin Model Controls';

    // Hide/Show toggle button
    const toggleButton = document.createElement('button');
    toggleButton.textContent = '▼ Hide';
    toggleButton.style.cssText = `
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: none;
        border-radius: 4px;
        padding: 4px 12px;
        font-size: 12px;
        cursor: pointer;
        transition: background 0.2s;
    `;
    toggleButton.addEventListener('mouseover', () => {
        toggleButton.style.background = 'rgba(255, 255, 255, 0.3)';
    });
    toggleButton.addEventListener('mouseout', () => {
        toggleButton.style.background = 'rgba(255, 255, 255, 0.2)';
    });

    titleRow.appendChild(title);
    titleRow.appendChild(toggleButton);

    // Create collapsible content container
    const contentContainer = document.createElement('div');
    contentContainer.id = 'itwin-controls-content';
    contentContainer.style.cssText = `
        transition: max-height 0.3s ease, opacity 0.3s ease;
        max-height: 1000px;
        opacity: 1;
        overflow: hidden;
    `;

    // Toggle functionality
    let isCollapsed = false;
    toggleButton.addEventListener('click', () => {
        isCollapsed = !isCollapsed;
        if (isCollapsed) {
            contentContainer.style.maxHeight = '0';
            contentContainer.style.opacity = '0';
            toggleButton.textContent = '▶ Show';
        } else {
            contentContainer.style.maxHeight = '1000px';
            contentContainer.style.opacity = '1';
            toggleButton.textContent = '▼ Hide';
        }
    });

    // Helper function to create a slider group
    const createSlider = (id, labelText, min, max, defaultValue, unit, callback) => {
        const group = document.createElement('div');
        group.style.cssText = 'margin-bottom: 20px;';

        const label = document.createElement('div');
        label.id = `${id}-label`;
        label.style.cssText = `
            margin-bottom: 5px;
            font-size: 14px;
            font-weight: 500;
        `;
        label.textContent = `${labelText}: ${defaultValue}${unit}`;

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.id = id;
        slider.min = min;
        slider.max = max;
        slider.value = defaultValue;
        slider.step = '1';
        slider.style.cssText = `
            width: 100%;
            margin-top: 5px;
            accent-color: #1e90ff;
        `;

        slider.addEventListener('input', (e) => {
            const value = parseInt(e.target.value, 10);
            const sign = value >= 0 ? '+' : '';
            label.textContent = `${labelText}: ${sign}${value}${unit}`;
            callback(value);
        });

        group.appendChild(label);
        group.appendChild(slider);
        return group;
    };

    // Height slider
    const heightSlider = createSlider(
        'itwin-height-slider',
        'Model Height',
        '-200',
        '200',
        relative_height_start.toString(),
        'm',
        (value) => window.relocateITwinModel(value)
    );

    // Rotation X slider (pitch - rotation around east axis)
    const rotationXSlider = createSlider(
        'itwin-rotation-x-slider',
        'Rotation X (Pitch)',
        '-180',
        '180',
        rotationX_start.toString(),
        '°',
        (value) => {
            window.itwinModelData.rotationX = value;
            window.rotateITwinModel(
                window.itwinModelData.rotationX,
                window.itwinModelData.rotationY || 0,
                window.itwinModelData.rotationZ || 0
            );
        }
    );

    // Rotation Y slider (roll - rotation around north axis)
    const rotationYSlider = createSlider(
        'itwin-rotation-y-slider',
        'Rotation Y (Roll)',
        '-180',
        '180',
        rotationY_start.toString(),
        '°',
        (value) => {
            window.itwinModelData.rotationY = value;
            window.rotateITwinModel(
                window.itwinModelData.rotationX || 0,
                window.itwinModelData.rotationY,
                window.itwinModelData.rotationZ || 0
            );
        }
    );

    // Rotation Z slider (heading - rotation around up axis)
    const rotationZSlider = createSlider(
        'itwin-rotation-z-slider',
        'Rotation Z (Heading)',
        '-180',
        '180',
        rotationZ_start.toString(),
        '°',
        (value) => {
            window.itwinModelData.rotationZ = value;
            window.rotateITwinModel(
                window.itwinModelData.rotationX || 0,
                window.itwinModelData.rotationY || 0,
                window.itwinModelData.rotationZ
            );
        }
    );

    // Button container (Reset + Save)
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
        display: flex;
        gap: 10px;
        margin-top: 10px;
    `;

    // Reset button
    const resetButton = document.createElement('button');
    resetButton.textContent = 'Reset All';
    resetButton.style.cssText = `
        flex: 1;
        padding: 8px 16px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
    `;
    resetButton.addEventListener('mouseover', () => {
        resetButton.style.background = '#5a6268';
    });
    resetButton.addEventListener('mouseout', () => {
        resetButton.style.background = '#6c757d';
    });
    resetButton.addEventListener('click', () => {
        // Reset all sliders
        shadowRoot.querySelector('#itwin-height-slider').value = '0';
        shadowRoot.querySelector('#itwin-rotation-x-slider').value = '0';
        shadowRoot.querySelector('#itwin-rotation-y-slider').value = '0';
        shadowRoot.querySelector('#itwin-rotation-z-slider').value = '0';

        // Update labels
        shadowRoot.querySelector('#itwin-height-slider-label').textContent = 'Model Height: 0m';
        shadowRoot.querySelector('#itwin-rotation-x-slider-label').textContent = 'Rotation X (Pitch): 0°';
        shadowRoot.querySelector('#itwin-rotation-y-slider-label').textContent = 'Rotation Y (Roll): 0°';
        shadowRoot.querySelector('#itwin-rotation-z-slider-label').textContent = 'Rotation Z (Heading): 0°';

        // Reset model
        window.itwinModelData.rotationX = 0;
        window.itwinModelData.rotationY = 0;
        window.itwinModelData.rotationZ = 0;
        window.relocateITwinModel(0);
    });

    // Save button
    const saveButton = document.createElement('button');
    saveButton.textContent = 'Save';
    saveButton.style.cssText = `
        flex: 1;
        padding: 8px 16px;
        background: #28a745;
        color: white;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
    `;
    saveButton.addEventListener('mouseover', () => {
        saveButton.style.background = '#218838';
    });
    saveButton.addEventListener('mouseout', () => {
        saveButton.style.background = '#28a745';
    });
    saveButton.addEventListener('click', async () => {
        try {
            // Disable button during save
            saveButton.disabled = true;
            saveButton.textContent = 'Saving...';
            saveButton.style.background = '#6c757d';

            const requestBody = {
                sceneId: window.sceneId,
                relative_height: window.itwinModelData.currentHeightOffset || 0,
                rotationX: window.itwinModelData.rotationX || 0,
                rotationY: window.itwinModelData.rotationY || 0,
                rotationZ: window.itwinModelData.rotationZ || 0
            };

            console.log('💾 Saving iTwin orientation:', requestBody);

            const response = await fetch(`${getBaseUrl()}/twinworld/saveOrientation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Failed to save: ${response.status}`);
            }

            const result = await response.json();
            console.log('✅ Orientation saved successfully:', result);

            // Show success feedback
            saveButton.textContent = '✓ Saved';
            saveButton.style.background = '#28a745';

            // Reset button after 2 seconds
            setTimeout(() => {
                saveButton.textContent = 'Save';
                saveButton.disabled = false;
            }, 2000);

        } catch (error) {
            console.error('❌ Failed to save orientation:', error);

            // Show error feedback
            saveButton.textContent = '✗ Failed';
            saveButton.style.background = '#dc3545';

            // Reset button after 2 seconds
            setTimeout(() => {
                saveButton.textContent = 'Save';
                saveButton.style.background = '#28a745';
                saveButton.disabled = false;
            }, 2000);
        }
    });

    // Assemble button container
    buttonContainer.appendChild(resetButton);
    buttonContainer.appendChild(saveButton);

    // Assemble content container
    contentContainer.appendChild(heightSlider);
    contentContainer.appendChild(rotationXSlider);
    contentContainer.appendChild(rotationYSlider);
    contentContainer.appendChild(rotationZSlider);
    contentContainer.appendChild(buttonContainer);

    // Assemble all controls
    controlsContainer.appendChild(titleRow);
    controlsContainer.appendChild(contentContainer);

    // Add to shadow DOM
    shadowRoot.appendChild(controlsContainer);

    console.log('✅ iTwin controls panel shown (height + rotation + save)');
}

/**
 * Hide iTwin model controls panel
 */
export function hideITwinHeightSlider() {
    const shadowRoot = document.querySelector('twinworld-app')?.shadowRoot;
    if (!shadowRoot) {
        return;
    }

    const existingContainer = shadowRoot.querySelector('#itwin-controls-container');
    if (existingContainer) {
        existingContainer.remove();
        console.log('✅ iTwin controls hidden');
    }
}

// ============================================================================
// SCENE LOADING
// ============================================================================

window.itwinTileset = null;

/**
 * Load iTwin scene with modular configuration
 * @param {object} itwinConfig - iTwin configuration from backend API
 * @param {object} sceneMetadata - Scene metadata containing name, coordinates, etc.
 */
window.loadITwinScene = async (itwinConfig, sceneMetadata) => {
    console.log('🔷 Loading iTwin scene:', sceneMetadata.name);

    const {
        clientId,           // Provided by backend API (not hardcoded)
        secret,             // Provided by backend API (not hardcoded)
        iModelId,
        iTwinId,
        ionToken,
        realityDataId = null,
        cesiumVersion = '1.135',
        defaultCamera = null
    } = itwinConfig;

    window.scenetype = 'itwin';

    try {
        let container;
        // 1. Destroy existing viewer
        if (window.viewer) {
            container = window.viewer.container;
            window.viewer.destroy();
            window.viewer = undefined;
        }

        // 2. Load iTwin-compatible Cesium
        const Cesium = await loadCesiumForITwin(cesiumVersion);
        console.log('✅ Loaded iTwin-compatible Cesium version:', cesiumVersion);

        // 3. GET FRESH ACCESS TOKEN VIA BACKEND PROXY
        console.log('🔑 Requesting iTwin access token via backend proxy...');

        const tokenResponse = await fetch(`${getBaseUrl()}/twinworld/itwin/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                clientId: clientId,
                clientSecret: secret
            })
        });

        if (!tokenResponse.ok) {
            const errorData = await tokenResponse.json();
            throw new Error(`Failed to get iTwin token: ${tokenResponse.status} - ${errorData.error}`);
        }

        const tokenData = await tokenResponse.json();
        const accessToken = tokenData.access_token;

        console.log('✅ Got iTwin access token via proxy');
        console.log('   Token type:', tokenData.token_type);
        console.log('   Expires in:', tokenData.expires_in, 'seconds');

        // 4. CONFIGURE AUTHENTICATION WITH FRESH TOKEN
        Cesium.Ion.defaultAccessToken = ionToken;            // For Cesium Ion assets (terrain, imagery)
        Cesium.ITwinPlatform.defaultAccessToken = accessToken; // For iTwin API calls

        if (!container) {
            throw new Error('Container not found in viewer');
        }

        window.viewer = new Cesium.Viewer(container, {
            terrain: Cesium.Terrain.fromWorldTerrain(),
            animation: false,
            timeline: false
        });

        // 5. LOAD IMODEL TILESET WITH ACCESS TOKEN
        console.log('📦 Loading iModel tileset...');
        const iModelTileset = await Cesium.ITwinData.createTilesetFromIModelId({
            iModelId: iModelId,
            iTwinId: iTwinId,
            accessToken: accessToken
        });
        viewer.scene.globe.show = true;
        window.viewer.scene.primitives.add(iModelTileset);

        // AWAIT THE READY PROMISE
        await iModelTileset.readyPromise;

        console.log('Tileset ready, applying relocation...');

        // Target location
        const targetLon = sceneMetadata.longitude;
        const targetLat = sceneMetadata.latitude;

        console.log('Relocating model to ground...');

        // Store the ORIGINAL model matrix (contains model's internal transform)
        const originalModelMatrix = Cesium.Matrix4.clone(iModelTileset.modelMatrix, new Cesium.Matrix4());

        // Get the model's current position
        const modelCenter = iModelTileset.boundingSphere.center.clone();
        const modelRadius = iModelTileset.boundingSphere.radius;

        // Convert to lat/lon/height
        const modelCenterCarto = Cesium.Cartographic.fromCartesian(modelCenter);
        const modelAltitude = modelCenterCarto.height;
        const modelLat = Cesium.Math.toDegrees(modelCenterCarto.latitude);
        const modelLon = Cesium.Math.toDegrees(modelCenterCarto.longitude);

        console.log('Model original altitude:', modelAltitude, 'meters');
        console.log('Model original lat/lon:', modelLat, modelLon);
        console.log('Model radius:', modelRadius, 'meters');

        // Sample terrain at BOTH locations
        const terrainProvider = window.viewer.terrainProvider;
        const originalCartographic = Cesium.Cartographic.fromDegrees(modelLon, modelLat);
        const targetCartographic = Cesium.Cartographic.fromDegrees(targetLon, targetLat);

        Cesium.sampleTerrainMostDetailed(terrainProvider, [originalCartographic, targetCartographic]).then((updatedPositions) => {
            const originalTerrainHeight = updatedPositions[0].height || 0;
            const targetTerrainHeight = updatedPositions[1].height || 0;

            const relativeHeightAboveTerrain = modelAltitude - originalTerrainHeight;
            let relative_height = sceneMetadata?.relative_height || 0;
            const targetHeight = targetTerrainHeight + relativeHeightAboveTerrain + relative_height;

            console.log('Original terrain:', originalTerrainHeight, 'm');
            console.log('Target terrain:', targetTerrainHeight, 'm');
            console.log('Relative height:', relativeHeightAboveTerrain, 'm');
            console.log('Target height:', targetHeight, 'm');

            // Create ENU transformation at target location
            const targetPosition = Cesium.Cartesian3.fromDegrees(targetLon, targetLat, targetHeight);
            const targetENU = Cesium.Transforms.eastNorthUpToFixedFrame(targetPosition);

            // Create ENU transformation at original location
            const originalPosition = Cesium.Cartesian3.fromDegrees(modelLon, modelLat, modelAltitude);
            const originalENU = Cesium.Transforms.eastNorthUpToFixedFrame(originalPosition);

            // Extract the LOCAL transformation (model's rotation/scale relative to its position)
            const originalENUInverse = Cesium.Matrix4.inverse(originalENU, new Cesium.Matrix4());
            const localTransform = Cesium.Matrix4.multiply(
                originalENUInverse,
                originalModelMatrix,
                new Cesium.Matrix4()
            );

            // Apply the local transform in the new ENU frame
            const finalMatrix = Cesium.Matrix4.multiply(
                targetENU,
                localTransform,
                new Cesium.Matrix4()
            );

            iModelTileset.modelMatrix = finalMatrix;
            let rotationX = sceneMetadata?.rotationX || 0;
            let rotationY = sceneMetadata?.rotationY || 0;
            let rotationZ = sceneMetadata?.rotationZ || 0;

            console.log('✅ Model relocated to:', targetLat, targetLon);
            console.log('   Height:', targetHeight, 'm (terrain +', relativeHeightAboveTerrain, 'm)');
            console.log('   Model properly oriented to Earth surface');

            // Store values globally for dynamic relocation
            window.itwinModelData = {
                originalTerrainHeight,
                targetTerrainHeight,
                baseRelativeHeight: relativeHeightAboveTerrain,
                localTransform,
                modelLon: targetLon,
                modelLat: targetLat,
                rotationX: rotationX,
                rotationY: rotationY,
                rotationZ: rotationZ,
                currentHeightOffset: relative_height
            };
            window.rotateITwinModel(rotationX, rotationY, rotationZ);

            // Show the height/rotation controls
            showITwinHeightSlider(relative_height, rotationX, rotationY, rotationZ);

            // Zoom to tileset
            window.viewer.zoomTo(iModelTileset, new Cesium.HeadingPitchRange(
                0,
                -0.5,
                Math.min(modelRadius * 2.0, 1000)
            ));
        });

        window.itwinTileset = iModelTileset;

        console.log('✅ iModel tileset loaded');

        // 6. Load reality mesh (optional)
        if (realityDataId) {
            console.log('🌍 Loading reality mesh...');
            const realityMesh = await Cesium.ITwinData.createTilesetForRealityDataId({
                iTwinId: iTwinId,
                realityDataId: realityDataId,
                accessToken: accessToken
            });
            window.viewer.scene.primitives.add(realityMesh);
            console.log('✅ Reality mesh loaded');
        }

        console.log('✅ iTwin scene loaded successfully');
    } catch (error) {
        console.error('❌ Failed to load iTwin scene:', error);
        throw error;
    }
};

// ============================================================================
// MODEL TRANSFORMATION
// ============================================================================

/**
 * Relocate iTwin model with height offset
 * @param {number} heightOffset - Height offset in meters (relative to base height)
 */
window.relocateITwinModel = (heightOffset) => {
    if (!window.itwinTileset) {
        console.warn('No iTwin tileset loaded');
        return;
    }

    if (!window.itwinModelData) {
        console.warn('Required iTwin relocation data not available');
        return;
    }

    const Cesium = window.Cesium;
    if (!Cesium) {
        console.warn('Cesium not available');
        return;
    }

    // Store current height offset
    window.itwinModelData.currentHeightOffset = heightOffset;

    // Calculate new target height with offset
    const newTargetHeight = window.itwinModelData.targetTerrainHeight +
                            window.itwinModelData.baseRelativeHeight +
                            heightOffset;

    console.log('Relocating iTwin model with offset:', heightOffset, 'm');
    console.log('New target height:', newTargetHeight, 'm');

    // Create new target position with updated height
    const targetPosition = Cesium.Cartesian3.fromDegrees(
        window.itwinModelData.modelLon,
        window.itwinModelData.modelLat,
        newTargetHeight
    );

    // Create ENU transformation at new target location
    const targetENU = Cesium.Transforms.eastNorthUpToFixedFrame(targetPosition);

    // Apply current rotation if it exists
    let transformToApply = window.itwinModelData.localTransform;

    const hasRotation = window.itwinModelData.rotationX ||
                        window.itwinModelData.rotationY ||
                        window.itwinModelData.rotationZ;

    if (hasRotation) {
        const xRad = Cesium.Math.toRadians(window.itwinModelData.rotationX || 0);
        const yRad = Cesium.Math.toRadians(window.itwinModelData.rotationY || 0);
        const zRad = Cesium.Math.toRadians(window.itwinModelData.rotationZ || 0);

        // Create rotation as 4x4 matrix
        const hpr = new Cesium.HeadingPitchRoll(zRad, xRad, yRad);
        const rotationMatrix3 = Cesium.Matrix3.fromHeadingPitchRoll(hpr);
        const rotationMatrix4 = Cesium.Matrix4.fromRotationTranslation(
            rotationMatrix3,
            Cesium.Cartesian3.ZERO,
            new Cesium.Matrix4()
        );

        // Apply rotation before local transform
        transformToApply = Cesium.Matrix4.multiply(
            rotationMatrix4,
            window.itwinModelData.localTransform,
            new Cesium.Matrix4()
        );
    }

    // Apply the transform in the new ENU frame
    const finalMatrix = Cesium.Matrix4.multiply(
        targetENU,
        transformToApply,
        new Cesium.Matrix4()
    );

    window.itwinTileset.modelMatrix = finalMatrix;

    console.log('✅ Model relocated to height:', newTargetHeight, 'm');
};

/**
 * Rotate iTwin model around X, Y, Z axes
 * @param {number} rotationX - Rotation around X axis in degrees (pitch)
 * @param {number} rotationY - Rotation around Y axis in degrees (roll)
 * @param {number} rotationZ - Rotation around Z axis in degrees (heading)
 */
window.rotateITwinModel = (rotationX, rotationY, rotationZ) => {
    if (!window.itwinTileset || !window.itwinModelData) {
        console.warn('iTwin tileset or model data not available');
        return;
    }

    const Cesium = window.Cesium;
    if (!Cesium) {
        console.warn('Cesium not available');
        return;
    }

    // Store rotation state
    window.itwinModelData.rotationX = rotationX;
    window.itwinModelData.rotationY = rotationY;
    window.itwinModelData.rotationZ = rotationZ;

    // Convert degrees to radians
    const xRad = Cesium.Math.toRadians(rotationX);
    const yRad = Cesium.Math.toRadians(rotationY);
    const zRad = Cesium.Math.toRadians(rotationZ);

    console.log('🔄 Rotating iTwin model:', {
        X: `${rotationX}° (${xRad.toFixed(4)} rad)`,
        Y: `${rotationY}° (${yRad.toFixed(4)} rad)`,
        Z: `${rotationZ}° (${zRad.toFixed(4)} rad)`
    });

    // Create rotation as a 4x4 matrix
    const hpr = new Cesium.HeadingPitchRoll(zRad, xRad, yRad);
    const rotationMatrix3 = Cesium.Matrix3.fromHeadingPitchRoll(hpr);

    // Convert 3x3 rotation to 4x4 matrix
    const rotationMatrix4 = Cesium.Matrix4.fromRotationTranslation(
        rotationMatrix3,
        Cesium.Cartesian3.ZERO, // No translation, just rotation
        new Cesium.Matrix4()
    );

    // Get current height offset (if height slider is being used)
    const heightOffset = window.itwinModelData.currentHeightOffset || 0;
    const currentHeight = window.itwinModelData.targetTerrainHeight +
                          window.itwinModelData.baseRelativeHeight +
                          heightOffset;

    // Create target position with current height
    const targetPosition = Cesium.Cartesian3.fromDegrees(
        window.itwinModelData.modelLon,
        window.itwinModelData.modelLat,
        currentHeight
    );

    // Create ENU transformation at target location
    const targetENU = Cesium.Transforms.eastNorthUpToFixedFrame(targetPosition);

    // Apply rotation BEFORE local transform: ENU -> Rotation -> LocalTransform
    const rotatedLocal = Cesium.Matrix4.multiply(
        rotationMatrix4,
        window.itwinModelData.localTransform,
        new Cesium.Matrix4()
    );

    // Apply the combined transform in the ENU frame
    const finalMatrix = Cesium.Matrix4.multiply(
        targetENU,
        rotatedLocal,
        new Cesium.Matrix4()
    );

    window.itwinTileset.modelMatrix = finalMatrix;

    console.log('✅ Model rotated successfully');
};

// ============================================================================
// TERRAIN UTILITIES
// ============================================================================

/**
 * Get terrain height at a specific location
 * @param {number} longitude - Longitude in degrees
 * @param {number} latitude - Latitude in degrees
 * @param {Cesium.TerrainProvider} [terrainProvider] - Optional terrain provider (defaults to viewer's terrain)
 * @returns {Promise<number>} - Terrain height in meters above WGS84 ellipsoid
 */
window.getTerrainHeight = async function (longitude, latitude, terrainProvider = null) {
    try {
        const provider = terrainProvider || window.viewer?.terrainProvider;

        if (!provider) {
            console.warn('⚠️  No terrain provider available, returning 0');
            return 0;
        }

        const position = Cesium.Cartographic.fromDegrees(longitude, latitude);
        const updatedPositions = await Cesium.sampleTerrainMostDetailed(provider, [position]);

        const terrainHeight = updatedPositions[0].height || 0;

        console.log(`📍 Terrain height at (${latitude.toFixed(4)}, ${longitude.toFixed(4)}): ${terrainHeight.toFixed(2)}m`);

        return terrainHeight;

    } catch (error) {
        console.error('❌ Failed to get terrain height:', error);
        return 0; // Fallback to sea level
    }
};

/**
 * Get terrain height and add an offset above ground
 * @param {number} longitude - Longitude in degrees
 * @param {number} latitude - Latitude in degrees
 * @param {number} [offsetMeters=100] - Height offset above terrain in meters
 * @returns {Promise<number>} - Total height (terrain + offset) in meters
 */
window.getHeightAboveTerrain = async function (longitude, latitude, offsetMeters = 100) {
    const terrainHeight = await window.getTerrainHeight(longitude, latitude);
    const totalHeight = terrainHeight + offsetMeters;

    console.log(`🎯 Total height: ${totalHeight.toFixed(2)}m (terrain: ${terrainHeight.toFixed(2)}m + offset: ${offsetMeters}m)`);

    return totalHeight;
};
